import React, { useContext, useEffect, useRef, useState } from "react";
import { PositionedItemsContainer } from "./PositionedItemsContainer";
import { IFileProvider } from "../../../../api/fileProvider/IFileProvider";
import { AppContext } from "../../../../appContext";
import { CommonContextMenu } from "../../../../components/commonContextMenu/commonContextMenu";
import { FullAnalysisCrumbs } from "../fullAnalysis/FullAnalysisCrumbs";
import { IAnalysisFolder } from "../fullAnalysis/IAnalysisFolder";
import { MultiFileProvider } from "../../../../api/fileProvider/multiFileProvider";
import { Spinner } from "../../../../components/spinner/Spinner";
import { FullAnalysisSelectFilePopup } from "../fullAnalysis/FullAnalysisSelectFilePopup";
import { getDefaultFolderIconIndex } from "../../../../utils/folderIcons";
import gDriveIcon from "../../../../assets/files/gdrive.svg";
import { useDragFile } from "../../../../utils/useDragFile";
import { DesktopFileItem } from "./DesktopFileItem";
import { DropFileArea } from "./DropFileArea";
import { FullAnalysisFolderSettings } from "../fullAnalysis/FullAnalysisFolderSettings";
import { GFileProvider } from "../../../../api/fileProvider/gFileProvider";
import { GDriveConnectButton } from "../../../desktopPage/GDriveConnectButton";
import { FullAnalysisFile } from "../fullAnalysis/FullAnalysisFile";
import { useDesktopFileViewLoadProgress} from "./DesktopFileViewLoadProgress";
import { rectInRect } from "../../../../utils/inBox";
import { formatFileName } from "../../../../utils/dateFormats";
import { FileProviderViewer } from "../../../desktopPage/FileProviderViewer";
import { AnalysisProvider } from "../../../../api/fileProvider/analysisProvider";
import { GViewer } from "../../../desktopPage/GViewer";
import { DesktopProvider } from "../../../../api/fileProvider/desktopProvider";
import { PopupWrapper, CloseButton } from "../../../../components/styled/BasicBlocks";
import { GoodScroll } from "../../../desktopPage/GoodScroll";
import { AnalysisViewer } from "../../../desktopPage/AnalysisViewer";
import closeIcon from "../../../../assets/close.svg";
import { FileCombinationPopup } from "./FileCombinationPopup";
import "./DesktopFileView.css";

export const DesktopFileView = ()=>{
    const {user, clients} = useContext(AppContext);
    //const files = ['Notes', 'Drive', 'Other', 'Tony Soprano', 'Files'];
    const [positions, setPositions] = useState<{x: number, y: number}[]>([]);
    const [fileProvider, setFileProvider] = useState<IFileProvider>(null);
    const [clickLocked, setClickLocked] = useState<boolean>(false);
    let clickLockedFix = false;
    const [fix, setFix] = useState(0);
    const [showContextMenu, setShowContextMenu] = useState<{top: number, left: number}>();
    const [showFileContextMenu, setShowFileContextMenu] = useState<{file: IAnalysisFolder, position: {top: number, left: number}}>();
    const [showUploadFilePopup, setShowUploadFilePopup] = useState(false);
    const [showAnalysisFilePopup, setShowAnalysisFilePopup] = useState(false);
    const [showCreateAnalysisForm, setShowCreateAnalysisForm] = useState(false);
    const [analysisFileName, setAnalysisFileName] = useState('');
    const [analysisText, setAnalysisText] = useState('');
    const [showFolderSettings, setShowFolderSettings] = useState<IAnalysisFolder>(null);     
    const [fileForRename, setFileForRename] = useState<IAnalysisFolder>(null); 
    const [multiselectedFiles, setMultiselectedFiles] = useState<IAnalysisFolder[]>([]);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const windowDragFile = useDragFile();
    const [loadProgressController, loadProgressElement] = useDesktopFileViewLoadProgress();
    const [underDropFile, setUnderDropFile] = useState<IAnalysisFolder>(null);
    const [underDropFileForCombination, setUnderDropFileForCombination] = useState<IAnalysisFolder>(null);
    const [canDrop, setCanDrop] = useState<string>('');
    const [combinationPopup, setCombinationPopup] = useState<{sourceFile: IAnalysisFolder, targetFile: IAnalysisFolder, dropPosition?: {x: number, y: number}} | null>(null);
    const [isOperationLoading, setIsOperationLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [loadingOperationId, setLoadingOperationId] = useState<string | null>(null);
    const [trackingFileIds, setTrackingFileIds] = useState<string[]>([]); // Track specific files that should disappear
    const [navigationTargetId, setNavigationTargetId] = useState<string | null>(null); // Track target folder for navigation
    const [previousFolderItems, setPreviousFolderItems] = useState<IAnalysisFolder[] | null>(null); // Track previous folder contents for navigation
    const { translate, currentLanguage } = useContext(AppContext);

    // Add close functionality for analysis file popup
    const closeAnalysisFile = async () => {
        const parentFolder = fileProvider?.crumbs[fileProvider.crumbs.length - 2];
        if (parentFolder) {
            startOperation('close_analysis', undefined, parentFolder.id); // Track target folder for navigation
            try {
                await fileProvider.getFiles(parentFolder.id, parentFolder.name);
                // Loading will be hidden by useEffect when UI updates
            } catch (error) {
                showError(`Failed to close analysis. Please try again.`);
                console.error('Close analysis failed:', error);
                endOperation();
            }
        }
    };

    const handleAddFile = ()=>{
        if (fileProvider.providerName == 'g'){
            fileInputRef.current.click();
        } else {
            setShowUploadFilePopup(true);
        }
        setShowContextMenu(null);
    }

    const uploadRealFile = (file: File)=>{
        const fileId = file.name+file.size+Math.random();
        fileProvider.createFile(file, currentFolder.id, (current, total)=>{
            console.log('progress', current, total);
            loadProgressController.updateProgress({current, total, fileName: file.name, fileId: fileId})
        }).finally(()=>loadProgressController.removeProgress(fileId));
    }

    const handleDropFile = (file: File)=>{
        if (fileProvider.providerName == 'g'){
            uploadRealFile(file);
        } else {
            //setShowUploadFilePopup(true);
        }
        setShowContextMenu(null);
    }

    const handleAddAnalysis = ()=>{
        setShowCreateAnalysisForm(true);
        setAnalysisFileName('');
        setAnalysisText('');
        setShowContextMenu(null);
    }

    const handleCreateAnalysisFile = async ()=>{
        if (!analysisText.trim() || !analysisFileName.trim()) {
            showError(translate('analysis_error_empty'));
            return;
        }

        const analysisRecord = {
            name: analysisFileName,
            tags: [] as string[],
            date: new Date().toISOString(),
            action: 'analysis',
            relatedClientId: '',
            chunks: [{
                type: 'notes',
                data: {
                    iconUrl: '',
                    title: analysisText.substring(0, 50) + (analysisText.length > 50 ? '...' : ''),
                    date: new Date().toISOString()
                }
            }]
        };

        const newFile: IAnalysisFolder = {
            fileData: [analysisRecord],
            name: analysisFileName.endsWith('.analysis') ? analysisFileName : analysisFileName + '.analysis',
            iconIndex: 0,
            date: new Date(),
            mimeType: 'text/analysis',
            type: 'file'
        };

        startOperation('create_file');
        setShowCreateAnalysisForm(false);
        setAnalysisText('');
        setAnalysisFileName('');

        try {
            await fileProvider.createFolder(newFile, currentFolder.id);
            await fileProvider.getFiles(currentFolder.id);
            // Loading will be hidden by useEffect when UI updates
        } catch (error) {
            showError('Failed to create analysis file. Please try again.');
            console.error('Create analysis file failed:', error);
            endOperation();
        }
    }

    const handleAddFolder = async ()=>{
        const newFolderData: IAnalysisFolder = {
            folderItems: [],
            name: 'newFolder' + currentFolder.folderItems.length,
            iconIndex: getDefaultFolderIconIndex(),
            date: new Date(),
            type: 'folder',
        };
        
        startOperation('create_folder');
        setShowContextMenu(null);
        
        try {
            await fileProvider.createFolder(newFolderData, currentFolder.id);
            await fileProvider.getFiles(currentFolder.id);
            // Loading will be hidden by useEffect when UI updates
        } catch (error) {
            showError('Failed to create folder. Please try again.');
            console.error('Create folder failed:', error);
            endOperation();
        }
    }

    const handleRenameFile = (file: IAnalysisFolder)=>()=>{
        setFileForRename(file);
        setShowFileContextMenu(null);
    }

    const handleDeleteFile = (file: IAnalysisFolder)=> async ()=>{
        startOperation('delete_file', file.id); // Track the file being deleted
        setShowFileContextMenu(null);
        
        try {
            await fileProvider.deleteFile(file.id);
            await fileProvider.getFiles(currentFolder.id);
            // Loading will be hidden by useEffect when UI updates
        } catch (error) {
            showError(`Failed to delete "${file.name}". Please try again.`);
            console.error('Delete file failed:', error);
            endOperation();
        }
    }

    const handleDeleteMultiFiles = ()=> async ()=>{
        const filesToDelete = [...multiselectedFiles];
        startOperation('delete_multi', filesToDelete.map(f => f.id)); // Track all files being deleted
        setShowFileContextMenu(null);
        
        try {
            await Promise.all(filesToDelete.map(file => fileProvider.deleteFile(file.id)));
            await fileProvider.getFiles(currentFolder.id);
            // Loading will be hidden by useEffect when UI updates
        } catch (error) {
            showError(`Failed to delete ${filesToDelete.length} files. Please try again.`);
            console.error('Delete multiple files failed:', error);
            endOperation();
        }
    }

    const handleMoveFiles = async (sourceFile: IAnalysisFolder, targetFile: IAnalysisFolder, dropPosition?: {x: number, y: number})=>{
        // Check if dropping a file on another file
        if (targetFile && targetFile.type === 'file' && sourceFile.type === 'file') {
            // Show combination popup for file-on-file drop
            setCombinationPopup({sourceFile, targetFile, dropPosition});
            return;
        }
        
        // Check if dropping a folder onto a file - use smart positioning
        if (targetFile && targetFile.type === 'file' && sourceFile.type === 'folder') {
            const targetIndex = files.findIndex(f => f.id === targetFile.id);
            const sourceIndex = files.findIndex(f => f.id === sourceFile.id);
            
            if (targetIndex >= 0 && sourceIndex >= 0) {
                const targetPos = positions[targetIndex] || {x: 0, y: 0};
                
                // Use smart positioning to find nearest free space
                const newPosition = findNearestFreePosition(
                    targetPos.x, 
                    targetPos.y, 
                    sourceFile.id
                );
                
                setPositions(prev => prev.map((pos, i) => i === sourceIndex ? newPosition : pos));
                
                // Save position to localStorage
                const parsedMeta = getPositionsMeta();
                parsedMeta[sourceFile.id] = newPosition;
                setPositionsMeta(parsedMeta);
            }
            return;
        }
        
        // Original folder drop logic - move into folder
        if (!targetFile || targetFile.type != 'folder'){
            return;
        }
        
        // If moving files to the current folder (not changing folder), apply collision detection
        if (targetFile.id === currentFolder.id) {
            // Moving within the same folder - apply immediate positioning
            const sourceIndex = files.findIndex(f => f.id === sourceFile.id);
            if (sourceIndex >= 0) {
                // Use smart positioning to avoid overlaps
                const newPosition = findNearestFreePosition(0, 0, sourceFile.id);
                
                setPositions(prev => prev.map((pos, i) => i === sourceIndex ? newPosition : pos));
                
                // Save position to localStorage
                const parsedMeta = getPositionsMeta();
                parsedMeta[sourceFile.id] = newPosition;
                setPositionsMeta(parsedMeta);
            }
            return;
        }
        
        startOperation('move_file', sourceFile.id); // Track the file being moved
        
        try {
            await fileProvider.updateFile({...sourceFile, parent: targetFile.id});
            await fileProvider.getFiles(currentFolder.id);
            // Loading will be hidden by useEffect when UI updates
        } catch (error) {
            showError(`Failed to move "${sourceFile.name}". Please try again.`);
            console.error('Move file failed:', error);
            endOperation();
        }
    }

    const handleMoveFilesUp = async (file: IAnalysisFolder, folder: IAnalysisFolder)=>{
        // Determine which files to move
        const found = multiselectedFiles.find(mfile => mfile.id == file.id);
        const filesToMove = found ? [...multiselectedFiles] : [file];
        
        startOperation('move_up', filesToMove.map(f => f.id)); // Track all files being moved
        setShowFileContextMenu(null);
        
        if (!folder || folder.type != 'folder'){
            endOperation();
            return;
        }
        
        try {
            await Promise.all(filesToMove.map(fileToMove => 
                fileProvider.updateFile({...fileToMove, parent: folder.id})
            ));
            await fileProvider.getFiles(currentFolder.id);
            console.log('move up - success');
            // Loading will be hidden by useEffect when UI updates
        } catch (error) {
            showError(`Failed to move ${filesToMove.length} file(s) up. Please try again.`);
            console.error('Move files up failed:', error);
            endOperation();
        }
    }


    const handleDeleteFileFromGDrive = (file: IAnalysisFolder)=>()=>{
        console.log(file);
        const tempProvider = new GFileProvider();
        tempProvider.onAuthError = ()=>{
            tempProvider.connect().then(()=>{
                return tempProvider.deleteFile(file.link)
            }).then(()=>{
                return fileProvider.deleteFile(file.id);
            })
        };
        tempProvider.deleteFile(file.link).then(()=>{
            return fileProvider.deleteFile(file.id);
        });

        setShowFileContextMenu(null);
    }

    const handleOptionsFile = (file: IAnalysisFolder)=>()=>{
        setShowFolderSettings(file);
        setShowFileContextMenu(null);
    }

    const getPositionsMeta = ()=>{
        const meta = localStorage.getItem('filesMeta');
        let parsedMeta: Record<string, {x: number, y: number}>;
        try {
            if (!meta){
                throw new Error();
            }
            parsedMeta = JSON.parse(meta);
        } catch(e){
            parsedMeta = {};
        }
        return parsedMeta;
    }

    // Loading state helpers
    const showError = (message: string) => {
        setErrorMessage(message);
        setTimeout(() => setErrorMessage(null), 5000); // Auto-hide after 5 seconds
    };

    const startOperation = (operationId: string, fileIdsToTrack?: string | string[], targetFolderId?: string) => {
        setIsOperationLoading(true);
        setLoadingOperationId(operationId);
        
        if (fileIdsToTrack) {
            const idsArray = Array.isArray(fileIdsToTrack) ? fileIdsToTrack : [fileIdsToTrack];
            setTrackingFileIds(idsArray);
        } else {
            setTrackingFileIds([]);
        }
        
        if (targetFolderId !== undefined) {
            setNavigationTargetId(targetFolderId);
            // Capture current folder contents when navigation starts
            const captured = currentFolder?.folderItems?.map(item => ({...item})) || null;
            setPreviousFolderItems(captured);
        } else {
            setNavigationTargetId(null);
            setPreviousFolderItems(null);
        }
    };

    const endOperation = () => {
        setIsOperationLoading(false);
        setLoadingOperationId(null);
        setTrackingFileIds([]);
        setNavigationTargetId(null);
        setPreviousFolderItems(null);
    };

    // Smart free space detection
    const findNearestFreePosition = (targetX: number, targetY: number, sourceFileId?: string): {x: number, y: number} => {
        const fileSize = 80; // Desktop file width from CSS
        const margin = 20; // Margin between files
        const step = fileSize + margin;
        
        // Get all current file positions except the source file
        const occupiedPositions = files?.map((file, index) => {
            if (file.id === sourceFileId) return null; // Skip source file
            return positions[index] || {x: 0, y: 0};
        }).filter(pos => pos !== null) || [];

        // Check if position is free (no overlap with existing files)
        const isPositionFree = (x: number, y: number): boolean => {
            return !occupiedPositions.some(pos => {
                if (!pos) return false;
                return Math.abs(x - pos.x) < fileSize && Math.abs(y - pos.y) < fileSize;
            });
        };

        // Try the exact target position first
        if (isPositionFree(targetX, targetY)) {
            return {x: targetX, y: targetY};
        }

        // Search in expanding spiral pattern for nearest free position
        for (let radius = 1; radius <= 10; radius++) {
            const candidates: {x: number, y: number, distance: number}[] = [];
            
            // Check positions in a grid around the target
            for (let dx = -radius; dx <= radius; dx++) {
                for (let dy = -radius; dy <= radius; dy++) {
                    if (Math.abs(dx) === radius || Math.abs(dy) === radius) { // Only check perimeter
                        const x = targetX + dx * step;
                        const y = targetY + dy * step;
                        
                        // Ensure position is within bounds (not negative)
                        if (x >= 0 && y >= 0 && isPositionFree(x, y)) {
                            const distance = Math.sqrt(dx * dx + dy * dy);
                            candidates.push({x, y, distance});
                        }
                    }
                }
            }
            
            if (candidates.length > 0) {
                // Return the nearest candidate
                candidates.sort((a, b) => a.distance - b.distance);
                return {x: candidates[0].x, y: candidates[0].y};
            }
        }

        // Fallback: place to the right of target with some offset
        return {x: Math.max(0, targetX + step), y: Math.max(0, targetY + 20)};
    }

    // Handle collision detection for moved files
    const applyCollisionDetection = (fileIds: string[], parsedMeta: Record<string, {x: number, y: number}>) => {
        if (!files) return parsedMeta;
        
        const updatedMeta = {...parsedMeta};
        
        fileIds.forEach(fileId => {
            const fileIndex = files.findIndex(f => f.id === fileId);
            if (fileIndex === -1) return;
            
            const currentPosition = updatedMeta[fileId];
            if (!currentPosition) {
                // File doesn't have a stored position, assign default position with collision check
                const defaultPosition = findNearestFreePosition(0, 0, fileId);
                updatedMeta[fileId] = defaultPosition;
            } else {
                // File has a position, check if it conflicts with other files
                const hasCollision = files.some((otherFile, otherIndex) => {
                    if (otherFile.id === fileId) return false; // Skip self
                    
                    const otherPosition = updatedMeta[otherFile.id];
                    if (!otherPosition) return false;
                    
                    // Check for overlap (files are 80px wide with some margin)
                    const fileSize = 80;
                    return Math.abs(currentPosition.x - otherPosition.x) < fileSize && 
                           Math.abs(currentPosition.y - otherPosition.y) < fileSize;
                });
                
                if (hasCollision) {
                    // Find a new safe position
                    const newPosition = findNearestFreePosition(currentPosition.x, currentPosition.y, fileId);
                    updatedMeta[fileId] = newPosition;
                }
            }
        });
        
        return updatedMeta;
    }

    // Sorting algorithms for desktop files
    const applySortingLayout = (sortType: 'horizontal' | 'vertical' | 'horizontal-box' | 'vertical-box') => {
        if (!files) return;
        
        // Sort files by name first
        const sortedFiles = [...files].sort((a, b) => a.name.localeCompare(b.name));
        
        const fileSize = 80; // Desktop file width from CSS
        const margin = 20; // Margin between files
        const step = fileSize + margin;
        
        // Calculate breadcrumb height to start grid below it
        const breadcrumbHeight = 42; // Adjusted for single-line breadcrumbs + sorting buttons layout
        
        let newPositions: {x: number, y: number}[];
        
        switch (sortType) {
            case 'horizontal':
                // Left to right, then top to bottom
                newPositions = sortedFiles.map((_, index) => {
                    const cols = Math.floor(window.innerWidth / step) || 1;
                    const row = Math.floor(index / cols);
                    const col = index % cols;
                    return {
                        x: col * step,
                        y: breadcrumbHeight + row * step
                    };
                });
                break;
                
            case 'vertical':
                // Top to bottom, then left to right
                newPositions = sortedFiles.map((_, index) => {
                    const rows = Math.ceil(window.innerHeight / step) || 1;
                    const col = Math.floor(index / rows);
                    const row = index % rows;
                    return {
                        x: col * step,
                        y: breadcrumbHeight + row * step
                    };
                });
                break;
                
            case 'horizontal-box':
                // Square formation with horizontal logic
                newPositions = sortedFiles.map((_, index) => {
                    const boxSize = Math.ceil(Math.sqrt(sortedFiles.length));
                    const row = Math.floor(index / boxSize);
                    const col = index % boxSize;
                    return {
                        x: col * step,
                        y: breadcrumbHeight + row * step
                    };
                });
                break;
                
            case 'vertical-box':
                // Square formation with vertical logic
                newPositions = sortedFiles.map((_, index) => {
                    const boxSize = Math.ceil(Math.sqrt(sortedFiles.length));
                    const col = Math.floor(index / boxSize);
                    const row = index % boxSize;
                    return {
                        x: col * step,
                        y: breadcrumbHeight + row * step
                    };
                });
                break;
                
            default:
                return;
        }
        
        // Update positions and save to storage
        const parsedMeta = getPositionsMeta();
        sortedFiles.forEach((file, index) => {
            parsedMeta[file.id] = newPositions[index];
        });
        setPositionsMeta(parsedMeta);
        
        // Update component state
        const updatedPositions = files.map(file => {
            const sortedIndex = sortedFiles.findIndex(sf => sf.id === file.id);
            return sortedIndex >= 0 ? newPositions[sortedIndex] : {x: 0, y: 0};
        });
        setPositions(updatedPositions);
    };

    const setPositionsMeta = (positionsMeta: Record<string, {x: number, y: number}>)=>{
        localStorage.setItem('filesMeta', JSON.stringify(positionsMeta));
    }

    const handleResetPositions = ()=>{
        const parsedMeta = getPositionsMeta();
        files.forEach(it=>{
            delete parsedMeta[it.id];
        });
        setPositionsMeta(parsedMeta);
        const _positions = files?.map((it): any=>parsedMeta[it.id] || null) || [];
        setPositions(_positions);
        setShowContextMenu(null);
    }

    useEffect(()=>{
        const provider = new MultiFileProvider(DesktopProvider);
        provider.onChange = ()=>{
            setFix(last=>last+1);
        }
        setFileProvider(provider);
    }, []);

    useEffect(()=>{
        if(!clients){
            console.log('await for clients loaded for user exp.');
            return;
        }
        if (fileProvider && user){
            console.log('get files');
            fileProvider.getFiles();
        }
    }, [fileProvider, user, clients]);

    const currentFolder = fileProvider?.crumbs[fileProvider?.crumbs.length -1];
    const files = currentFolder?.folderItems.map(it=>it);

    useEffect(()=>{
        const _files = currentFolder?.folderItems.map(it=>it);
        if (!_files){
            return;
        }
        let parsedMeta = getPositionsMeta();
        
        // Apply collision detection for moved files
        if (trackingFileIds.length > 0 && (loadingOperationId === 'move_file' || loadingOperationId === 'move_up')) {
            // Find files that are currently being tracked (just moved)
            const movedFilesInFolder = trackingFileIds.filter(id => 
                _files.some(file => file.id === id)
            );
            
            if (movedFilesInFolder.length > 0) {
                // Apply collision detection and get updated positions
                parsedMeta = applyCollisionDetection(movedFilesInFolder, parsedMeta);
                // Save the updated positions
                setPositionsMeta(parsedMeta);
            }
        }
        
        // Apply collision detection for newly created files/folders without positions
        const newFilesWithoutPositions = _files.filter(file => !parsedMeta[file.id]);
        if (newFilesWithoutPositions.length > 0) {
            // Apply collision detection to ensure new files don't overlap
            const newFileIds = newFilesWithoutPositions.map(f => f.id);
            parsedMeta = applyCollisionDetection(newFileIds, parsedMeta);
            // Save the updated positions
            setPositionsMeta(parsedMeta);
        }
        
        const _positions = _files?.map((it): any=>parsedMeta[it.id] || null) || [];
        setPositions(_positions);
        
        // Hide loading when UI actually updates after operation
        if (isOperationLoading && loadingOperationId) {
            // If we're tracking specific files that should disappear, wait for ALL to be gone
            if (trackingFileIds.length > 0) {
                const currentFileIds = _files.map(file => file.id);
                const allTrackedFilesGone = trackingFileIds.every(trackId => !currentFileIds.includes(trackId));
                
                if (allTrackedFilesGone) {
                    // All tracked files are gone from UI - safe to hide cover
                    requestAnimationFrame(() => {
                        endOperation();
                    });
                }
                // If any tracked files still exist, keep cover visible
            } else {
                // No specific file tracking - navigation operation
                // Check if we have a navigation target and if we've reached it
                if (navigationTargetId !== null) {
                    // Wait until currentFolder.id matches the target folder ID AND folder contents have changed
                    const folderContentsChanged = previousFolderItems && (
                        !_files ||
                        _files.length !== previousFolderItems.length ||
                        _files.some((file, index) => file.id !== previousFolderItems[index]?.id)
                    );
                    
                    // Handle empty string IDs (root Desktop folder)
                    const currentId = currentFolder?.id ?? '';
                    const targetId = navigationTargetId ?? '';
                    
                    if (currentId === targetId && 
                        (!currentFolder?.loadingStatus || currentFolder.loadingStatus !== 'loading') &&
                        folderContentsChanged) {
                        requestAnimationFrame(() => {
                            endOperation();
                        });
                    }
                    // If not yet at target folder, still loading, or contents haven't changed, keep cover visible
                } else {
                    // No navigation target - general operation completion
                    if (!currentFolder?.loadingStatus || currentFolder.loadingStatus !== 'loading') {
                        requestAnimationFrame(() => {
                            endOperation();
                        });
                    }
                }
            }
        }
    }, [currentFolder, currentFolder?.loadingStatus, currentFolder?.folderItems, isOperationLoading, loadingOperationId, trackingFileIds, navigationTargetId, previousFolderItems]);

    const statusMap = {
        'error': (
            <GDriveConnectButton onClick={async ()=>{
                console.log('try connect');
                startOperation('connect_gdrive', undefined, currentFolder.id); // Track target folder for navigation
                try {
                    await fileProvider.connect();
                    console.log('connected');
                    await fileProvider.getFiles(currentFolder.id, currentFolder.name);
                    console.log('files received');
                    // Loading will be hidden by useEffect when UI updates
                } catch (error) {
                    showError(`Failed to connect to Google Drive. Please try again.`);
                    console.error('GDrive connect failed:', error);
                    endOperation();
                }
            }}></GDriveConnectButton>
        ),
        'loading': <Spinner></Spinner>
    }
    const statusElement = statusMap[currentFolder?.loadingStatus as keyof typeof statusMap];

    const fileItems = files ? files.map(file=><DesktopFileItem 
        isSelected={multiselectedFiles.includes(file)}
        isUnderDrop={file == underDropFile && file.type == 'folder'}
        isUnderFileDrop={file == underDropFileForCombination && file.type == 'file'}
        isRenaming={fileForRename == file} item={file}
        onRename={async (name)=>{
            if (file.name != name) {
                startOperation('rename_file'); // Rename doesn't remove file, so no tracking needed
                
                try {
                    await fileProvider.updateFile({...file, name: name});
                    await fileProvider.getFiles(currentFolder.id);
                    // Loading will be hidden by useEffect when UI updates
                } catch (error) {
                    showError(`Failed to rename "${file.name}". Please try again.`);
                    console.error('Rename file failed:', error);
                    endOperation();
                }
            }
            setFileForRename(null)
        }} 
        onContextMenu={(e)=>{
            e.stopPropagation();
            e.preventDefault();
            // Right-click should also select the file
            if (!multiselectedFiles.includes(file)) {
                setMultiselectedFiles([file]);
            }
            setShowFileContextMenu({file: file, position: {top: e.clientY, left: e.clientX}});
        }} 
        onMouseDown={()=>{
            // Mouse down - select the file immediately
            if (!multiselectedFiles.includes(file)) {
                setMultiselectedFiles([file]);
            }
        }}
        onDoubleClick={async ()=>{
            // Double click - open the file/folder
            if (!clickLocked && !clickLockedFix){
                startOperation('open_file', undefined, file.id); // Track target folder for navigation
                try {
                    // Small delay to ensure loading cover is visible
                    await new Promise(resolve => setTimeout(resolve, 50));
                    await fileProvider.getFiles(file.id);
                    // Loading will be hidden by useEffect when UI updates
                } catch (error) {
                    showError(`Failed to open "${file.name}". Please try again.`);
                    console.error('Open file/folder failed:', error);
                    endOperation();
                }
            } else {
                clickLockedFix = false;
                setClickLocked(false);
            }
        }}
    ></DesktopFileItem>) : [];

    const crumbBounds = useRef<Record<string, HTMLDivElement>>({});
    const dropAreaBounds = useRef<DOMRect[]>([]);

    return <div className="DesktopFileView" onContextMenu={(e)=>{
        e.preventDefault();
        setShowContextMenu({left: e.clientX, top: e.clientY});
        }}
    > 
        <div className="DesktopFileView_header" style={{marginBottom: "10px"}}>
            {fileProvider && <FullAnalysisCrumbs boundsRef={crumbBounds} dropData={{id: underDropFile?.id, canDrop}} crumbs={fileProvider?.crumbs} onCrumb={async (crumb: IAnalysisFolder) => {
                // Check if user is already in the target folder to avoid loading loop
                const currentFolderId = currentFolder?.id ?? '';
                const targetFolderId = crumb.id ?? '';
                
                if (currentFolderId === targetFolderId) {
                    // User is already in this folder, no need to navigate
                    return;
                }
                
                startOperation('navigate_crumb', undefined, crumb.id); // Track target folder for navigation
                try {
                    // Small delay to ensure loading cover is visible
                    await new Promise(resolve => setTimeout(resolve, 50));
                    await fileProvider.getFiles(crumb.id, crumb.name);
                    // Loading will be hidden by useEffect when UI updates
                } catch (error) {
                    showError(`Failed to navigate to "${crumb.name}". Please try again.`);
                    console.error('Breadcrumb navigation failed:', error);
                    endOperation();
                }
            }}></FullAnalysisCrumbs>}
            
            {/* Sorting buttons moved to the right corner */}
            {files && files.length > 0 && (
                <div className="DesktopFileView_sortButtons">
                    <div 
                        className="DesktopFileView_sortButton" 
                        onClick={() => applySortingLayout('horizontal')}
                        title="Sort horizontally (left to right, then top to bottom)"
                    >
                        <div className="DesktopFileView_sortIcon DesktopFileView_sortIcon--horizontal"></div>
                    </div>
                    <div 
                        className="DesktopFileView_sortButton" 
                        onClick={() => applySortingLayout('vertical')}
                        title="Sort vertically (top to bottom, then left to right)"
                    >
                        <div className="DesktopFileView_sortIcon DesktopFileView_sortIcon--vertical"></div>
                    </div>
                    <div 
                        className="DesktopFileView_sortButton" 
                        onClick={() => applySortingLayout('horizontal-box')}
                        title="Sort in horizontal box (square formation, horizontal order)"
                    >
                        <div className="DesktopFileView_sortIcon DesktopFileView_sortIcon--horizontalBox"></div>
                    </div>
                    <div 
                        className="DesktopFileView_sortButton" 
                        onClick={() => applySortingLayout('vertical-box')}
                        title="Sort in vertical box (square formation, vertical order)"
                    >
                        <div className="DesktopFileView_sortIcon DesktopFileView_sortIcon--verticalBox"></div>
                    </div>
                </div>
            )}
        </div>

        {fileProvider?.providerName == 'g' ? <div className="DesktopFileView_providerHeader">{translate('Files are located at gDrive')}</div> : ''}
        {statusElement}
        
        {/* Initial loading spinner */}
        {(!files || !clients || !user) && (
            <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '400px',
                width: '100%'
            }}>
                <Spinner />
            </div>
        )}
        
        {currentFolder?.type == 'file' && <div 
            style={{
                position: 'fixed',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                zIndex: 1000
            }}
            onClick={(e) => {
                // Close if clicking on the overlay background
                if (e.target === e.currentTarget) {
                    closeAnalysisFile();
                }
            }}
        >
            <div style={{position: 'relative', width: '90%', height: '90%', maxWidth: '1200px'}}>
                <PopupWrapper
                    className="DesktopFileView_fileWrapper"
                    onContextMenu={(e)=>{
                        e.stopPropagation();
                    }}
                    style={{
                        position: 'relative',
                        width: '100%',
                        height: '100%'
                    }}
                >
                    <CloseButton 
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            closeAnalysisFile();
                        }}
                        style={{
                            position: 'absolute',
                            top: '15px',
                            right: '15px',
                            zIndex: 20,
                            background: 'rgba(255, 255, 255, 0.9)',
                            borderRadius: '50%',
                            width: '32px',
                            height: '32px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            border: '1px solid #ddd',
                            backgroundImage: `url(${closeIcon})`,
                            backgroundSize: '16px 16px',
                            backgroundRepeat: 'no-repeat',
                            backgroundPosition: 'center'
                        }}
                    />
                    <GoodScroll>
                        <FullAnalysisFile className='DesktopFileView_fileView' file={currentFolder}>
                        </FullAnalysisFile>
                    </GoodScroll>
                </PopupWrapper>
            </div>
        </div>}
        {(files && currentFolder.type == 'folder') && <DropFileArea dragFile={windowDragFile} onDropFile={(file)=>{handleDropFile(file)}}><PositionedItemsContainer
            boundingsRef={dropAreaBounds}
            onMultiselect={(indexes)=>{
                const selectedFiles = indexes.map(ind=>files[ind]);
                setMultiselectedFiles(selectedFiles);
            }}
            muliselectIndexes={multiselectedFiles.map((it)=>files.indexOf(it))}
            onChangePosition={(index, newPosition, underDropIndex)=>{
                const crumbKey = Object.keys(crumbBounds.current).find(key=>{
                    const rect = crumbBounds.current[key].getBoundingClientRect();
                    const moving = dropAreaBounds.current[index];
                    return rectInRect(rect, moving);
                });
                const underCrumb = fileProvider.crumbs.find(it=>it.id == crumbKey);
                const cantDropOfWrongProvider = underCrumb && underCrumb.linkProvider != fileProvider?.providerName;
                const cantDropOfSameFolder = underCrumb && fileProvider.crumbs.indexOf(underCrumb) == fileProvider.crumbs.length - 1;

                const underFile = (!cantDropOfWrongProvider && !cantDropOfSameFolder && underCrumb) || files[underDropIndex];
                if (underFile){
                    console.log('drop to ', underFile);
                    
                    // Check if this is a folder being dropped on a file - smart positioning handled in handleMoveFiles
                    const isfolderOnFile = underFile.type === 'file' && files[index].type === 'folder';
                    const isFileOnFile = underFile.type === 'file' && files[index].type === 'file';
                    
                    handleMoveFiles(files[index], underFile);
                    
                    // Only update position manually if not handled by smart positioning logic
                    if (!isfolderOnFile && !isFileOnFile) {
                        setPositions(last=>{
                            return last.map((it, i)=> i == index ? newPosition : it);
                        });
                        const parsedMeta = getPositionsMeta();
                        parsedMeta[files[index].id] = newPosition;
                        setPositionsMeta(parsedMeta);
                    }
                } else {
                    // No target file - just update position normally
                    setPositions(last=>{
                        return last.map((it, i)=> i == index ? newPosition : it);
                    });
                    const parsedMeta = getPositionsMeta();
                    parsedMeta[files[index].id] = newPosition;
                    setPositionsMeta(parsedMeta);
                }
                setUnderDropFile(null);
                setUnderDropFileForCombination(null);
            }}
            onChangingPosition={(index, newPosition, underDropIndex)=>{
                const crumbKey = Object.keys(crumbBounds.current).find(key=>{
                    const rect = crumbBounds.current[key].getBoundingClientRect();
                    const moving = dropAreaBounds.current[index];
                    return rectInRect(rect, moving);
                });
                const underCrumb = fileProvider.crumbs.find(it=>it.id == crumbKey);
                const cantDropOfWrongProvider = underCrumb && underCrumb.linkProvider != fileProvider?.providerName;
                const cantDropOfSameFolder = underCrumb && fileProvider.crumbs.indexOf(underCrumb) == fileProvider.crumbs.length - 1;
                if (cantDropOfWrongProvider) {
                    setCanDrop('Cant drop out of drive');
                } else if (cantDropOfSameFolder) {
                    setCanDrop('Cant drop to same folder');
                } else {
                    setCanDrop('');
                }
                const underFile = files[underDropIndex];
                setUnderDropFile(underCrumb || (underFile && underFile.type === 'folder' ? underFile : null));
                setUnderDropFileForCombination(underFile && underFile.type === 'file' ? underFile : null);
                if (!clickLocked && !clickLockedFix){
                    clickLockedFix = true;
                    setClickLocked(true);
                }
            }}
            positions={positions}
            items={fileItems}
        ></PositionedItemsContainer></DropFileArea>}
        <CommonContextMenu 
            key={showContextMenu?.left+'-'+showContextMenu?.top}
            position={showContextMenu} 
            onClose={()=>setShowContextMenu(null)}
            boundingElement={document.body}
        >
            <div 
                className="button DesktopFileView_contextMenu_button DesktopFileView_contextMenu_button--add"
                onClick={handleAddFile}
            >
                {fileProvider?.providerName == 'g' ? translate('desktop_uploadFile') : translate('desktop_uploadGDrive')}
            </div>

            {fileProvider?.providerName == 'desktop' && <div 
                className="button DesktopFileView_contextMenu_button DesktopFileView_contextMenu_button--add" 
                onClick={handleAddAnalysis}
            >{translate('desktop_addAnalysis')}</div>}

            <div className="button DesktopFileView_contextMenu_button DesktopFileView_contextMenu_button--add" 
                onClick={handleAddFolder}
            >{translate('desktop_createFolder')}</div>

            <div className="button DesktopFileView_contextMenu_button DesktopFileView_contextMenu_button--delete" 
                onClick={handleResetPositions}
            >{translate('desktop_resetPositions').replace(' ', '\u00A0')}</div>
        </CommonContextMenu>
        <CommonContextMenu 
            key={'fc-'+showFileContextMenu?.position.left+'-'+showFileContextMenu?.position.top}
            position={showFileContextMenu?.position} 
            onClose={()=>setShowFileContextMenu(null)}
            boundingElement={document.body}
        >
            <div className="button DesktopFileView_contextMenu_button DesktopFileView_contextMenu_button--edit" 
                onClick={handleRenameFile(showFileContextMenu?.file)}
            >{translate('desktop_rename')}</div>

            <div className="button DesktopFileView_contextMenu_button DesktopFileView_contextMenu_button--delete"
                onClick={handleDeleteFile(showFileContextMenu?.file)}
            >{(translate('desktop_delete') as any)(formatFileName(showFileContextMenu?.file.name))}</div>

            {(multiselectedFiles && multiselectedFiles.length > 1) && <div 
                className="button DesktopFileView_contextMenu_button DesktopFileView_contextMenu_button--delete" 
                onClick={handleDeleteMultiFiles()}
            >{(translate('desktop_deleteSelectedFiles') as any)(multiselectedFiles.length)}</div>}

            {showFileContextMenu?.file?.linkProvider == 'g' && <div 
                className="button DesktopFileView_contextMenu_button DesktopFileView_contextMenu_button--delete"
                onClick={handleDeleteFileFromGDrive(showFileContextMenu?.file)}
            >{translate('desktop_deleteFromGDrive')}</div>}

            {
                fileProvider?.crumbs[fileProvider.crumbs.length-2]?.type=='folder' && 
                fileProvider?.crumbs[fileProvider.crumbs.length-2]?.linkProvider == fileProvider?.crumbs[fileProvider.crumbs.length-1]?.linkProvider && 
                <div className="button DesktopFileView_contextMenu_button DesktopFileView_contextMenu_button--edit" 
                    onClick={async ()=>{
                        await handleMoveFilesUp(showFileContextMenu?.file, fileProvider.crumbs[fileProvider.crumbs.length-2])
                    }}
                >
                    {translate('desktop_moveFilesUp')}
                </div>
            }

            <div className="button DesktopFileView_contextMenu_button DesktopFileView_contextMenu_button--more"
                onClick={handleOptionsFile(showFileContextMenu?.file)}
            >{translate('desktop_options')}</div>
        </CommonContextMenu>
        {showUploadFilePopup && <FullAnalysisSelectFilePopup
            FileViewer={GViewer}
            onClose={()=>{
                setShowUploadFilePopup(false)
            }}
            onSubmit={(item)=>{
                setShowUploadFilePopup(false);
                if (fileProvider.providerName != 'g'){
                    fileProvider.createFolder({
                        iconIndex: item.iconIndex || 0,
                        name: item.name,
                        date: new Date(),
                        type: item.type == 'file' ? 'fileLink' : 'folderLink',
                        link: item.id,
                        linkProvider: 'g'
                    }, currentFolder.id);
                }
            }}
        >
        </FullAnalysisSelectFilePopup>}
        {showAnalysisFilePopup && <FullAnalysisSelectFilePopup
            FileViewer={AnalysisViewer}
            title='Select analysis file'
            onClose={()=>{
                setShowAnalysisFilePopup(false)
            }}
            onSubmit={(item)=>{
                setShowAnalysisFilePopup(false);
                //if (fileProvider.providerName == ''){
                    fileProvider.createFolder({
                        iconIndex: item.iconIndex || 0,
                        name: item.name,
                        date: new Date(),
                        type: item.type == 'file' ? 'fileLink' : 'folderLink',
                        link: item.id,
                        linkProvider: 'analysis'
                    }, currentFolder.id);
                //}
            }}
        >
        </FullAnalysisSelectFilePopup>}

        {showCreateAnalysisForm && (
            <div className="popup" style={{
                position: 'fixed',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 1000
            }}>
                <div style={{
                    backgroundColor: 'white',
                    padding: '20px',
                    borderRadius: '8px',
                    maxWidth: '500px',
                    width: '90%',
                    maxHeight: '80%',
                    overflow: 'auto'
                }}>
                    <h3 style={{marginTop: 0, marginBottom: '15px'}}>{translate('analysis_add_file')}</h3>
                    
                    <div style={{marginBottom: '15px'}}>
                        <label style={{display: 'block', marginBottom: '5px', fontWeight: 'bold'}}>
                            {translate('analysis_file_name')}:
                        </label>
                        <input 
                            type="text" 
                            value={analysisFileName}
                            onChange={(e) => setAnalysisFileName(e.target.value)}
                            placeholder={translate('analysis_file_placeholder')}
                            style={{
                                width: '100%',
                                padding: '8px',
                                borderRadius: '4px',
                                border: '1px solid #ddd',
                                fontSize: '14px'
                            }}
                        />
                    </div>
                    
                    <div style={{marginBottom: '20px'}}>
                        <label style={{display: 'block', marginBottom: '5px', fontWeight: 'bold'}}>
                            {translate('analysis_content')}:
                        </label>
                        <textarea 
                            value={analysisText}
                            onChange={(e) => setAnalysisText(e.target.value)}
                            placeholder={translate('analysis_content_placeholder')}
                            rows={6}
                            style={{
                                width: '100%',
                                padding: '8px',
                                borderRadius: '4px',
                                border: '1px solid #ddd',
                                resize: 'vertical',
                                fontSize: '14px'
                            }}
                        />
                    </div>
                    
                    <div style={{display: 'flex', gap: '10px', justifyContent: 'flex-end'}}>
                        <button 
                            onClick={() => setShowCreateAnalysisForm(false)}
                            style={{
                                padding: '10px 20px',
                                backgroundColor: '#6c757d',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontSize: '14px'
                            }}
                        >
                            {translate('common_cancel')}
                        </button>
                        <button 
                            onClick={handleCreateAnalysisFile}
                            style={{
                                padding: '10px 20px',
                                backgroundColor: '#007bff',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontSize: '14px'
                            }}
                        >
                            {translate('analysis_create_file')}
                        </button>
                    </div>
                </div>
            </div>
        )}
        {showFolderSettings && <FullAnalysisFolderSettings 
            folder={showFolderSettings}
            editableOptions={fileProvider?.providerName == 'g' ? {icon: false} : undefined}
            onClose={()=>{
                setShowFolderSettings(null)
            }}
            onSubmit={(data)=>{
                setShowFolderSettings(null);
                fileProvider.updateFile({...showFolderSettings, ...data});
            }}
        >
        </FullAnalysisFolderSettings>}
        {<input ref={fileInputRef} type="file" style={{display: "none"}} onChange={(e)=>{
            uploadRealFile(e.target.files[0]);
        }}></input>}

        {loadProgressElement}
        
        {/* Loading overlay */}
        {isOperationLoading && (
            <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'rgba(255, 255, 255, 0.8)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 1000,
                backdropFilter: 'blur(2px)'
            }}>
                <div style={{
                    background: 'white',
                    padding: '20px 30px',
                    borderRadius: '8px',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    fontSize: '16px',
                    color: '#333'
                }}>
                    <div style={{
                        width: '20px',
                        height: '20px',
                        border: '2px solid #e0e0e0',
                        borderTop: '2px solid #2196F3',
                        borderRadius: '50%',
                        animation: 'spin 1s linear infinite'
                    }}></div>
                    {translate('session_processing')}
                </div>
            </div>
        )}

        {errorMessage && (
            <div style={{
                position: 'fixed',
                top: '20px',
                right: '20px',
                background: '#f44336',
                color: 'white',
                padding: '12px 16px',
                borderRadius: '4px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
                zIndex: 1001,
                maxWidth: '300px',
                fontSize: '14px',
                animation: 'slideInRight 0.3s ease'
            }}>
                {errorMessage}
                <button 
                    onClick={() => setErrorMessage(null)}
                    style={{
                        background: 'none',
                        border: 'none',
                        color: 'white',
                        marginLeft: '12px',
                        cursor: 'pointer',
                        fontSize: '16px',
                        padding: '0'
                    }}
                >
                    ×
                </button>
            </div>
        )}
        
        {combinationPopup && <FileCombinationPopup
            sourceFile={combinationPopup.sourceFile}
            targetFile={combinationPopup.targetFile}
            onYes={() => {
                // Combine files - append source to target
                console.log('Combining files:', combinationPopup.sourceFile.name, 'into', combinationPopup.targetFile.name);
                // TODO: Implement actual file combination logic
                setCombinationPopup(null);
            }}
            onNo={() => {
                // Place file in nearby location using smart positioning
                const sourceFile = combinationPopup.sourceFile;
                const targetFile = combinationPopup.targetFile;
                
                const targetIndex = files.findIndex(f => f.id === targetFile.id);
                const sourceIndex = files.findIndex(f => f.id === sourceFile.id);
                
                if (targetIndex >= 0 && sourceIndex >= 0) {
                    const targetPos = positions[targetIndex] || {x: 0, y: 0};
                    
                    // Use smart positioning to find nearest free space
                    const newPosition = findNearestFreePosition(
                        targetPos.x, 
                        targetPos.y, 
                        sourceFile.id
                    );
                    
                    setPositions(prev => prev.map((pos, i) => i === sourceIndex ? newPosition : pos));
                    
                    // Save position to localStorage
                    const parsedMeta = getPositionsMeta();
                    parsedMeta[sourceFile.id] = newPosition;
                    setPositionsMeta(parsedMeta);
                }
                
                setCombinationPopup(null);
            }}
            onClose={() => {
                setCombinationPopup(null);
            }}
        />}
    </div>
}